﻿import { AnimatePresence, motion } from "framer-motion";
import {
  DollarSign,
  Edit,
  Percent,
  Phone,
  Shield,
  UserCheck,
  Users,
  UserX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export function TeamGrid({
  collaborators,
  teamOrders,
  isLoading,
  setEditingCollab,
  form,
  setIsModalOpen,
  toggleStatus,
  setPayCollabId,
  setPayAmount,
}: {
  collaborators: any[];
  teamOrders: any[];
  isLoading: boolean;
  setEditingCollab: (c: any) => void;
  form: any;
  setIsModalOpen: (open: boolean) => void;
  toggleStatus: any;
  setPayCollabId: (id: string) => void;
  setPayAmount: (val: number) => void;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={`tskel-${i}`}
            className="bg-slate-900 border border-slate-800 rounded-xl p-5 shadow-sm space-y-4"
          >
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-20 rounded-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-8 w-8 rounded-full" />
              </div>
            </div>
            <div className="space-y-3 pt-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <AnimatePresence>
        {collaborators.map((collab) => {
          const isAtivo = collab.status === "ATIVO";
          return (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              key={collab.id}
              className={`relative bg-slate-900 border rounded-xl p-5 shadow-sm transition-all
                  ${isAtivo ? "border-slate-800 hover:border-slate-700" : "border-slate-800/50 opacity-60"}
                `}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-bold text-lg text-slate-100">
                    {collab.name}
                  </h3>
                  <span
                    className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      collab.system_role === "ADMIN"
                        ? "bg-purple-500/20 text-purple-400"
                        : "bg-blue-500/20 text-blue-400"
                    }`}
                  >
                    {collab.system_role}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCollab(collab);
                      form.reset({
                        name: collab.name,
                        phone: collab.phone || "",
                        specialty: collab.specialty || "",
                        system_role: collab.system_role as any,
                        commission_rate: collab.commission_rate,
                        status: collab.status as any,
                      });
                      setIsModalOpen(true);
                    }}
                    className="p-2 rounded-full transition-colors bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                    title="Editar funcionário"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() =>
                      toggleStatus.mutate({
                        id: collab.id,
                        newStatus: isAtivo ? "INATIVO" : "ATIVO",
                      })
                    }
                    title={
                      isAtivo ? "Desativar funcionário" : "Reativar funcionário"
                    }
                    className={`p-2 rounded-full transition-colors ${
                      isAtivo
                        ? "bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                        : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                    }`}
                  >
                    {isAtivo ? <UserCheck size={18} /> : <UserX size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Phone size={14} className="text-slate-500" />
                  {collab.phone || "Sem telefone"}
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Shield size={14} className="text-slate-500" />
                  {collab.specialty || "Geral"}
                </div>
              </div>

              {collab.system_role === "COLABORADOR" && (
                <>
                  <div className="flex justify-between items-center pt-3 border-t border-slate-800">
                    <span className="text-slate-500 text-sm">
                      Comissão Base:
                    </span>
                    <span className="flex items-center gap-1 font-bold text-blue-400">
                      {collab.commission_rate} <Percent size={14} />
                    </span>
                  </div>

                  {(() => {
                    const collabOrders = teamOrders.filter(
                      (o) => o.assigned_to === collab.id,
                    );

                    // -- Lógica de Comissão --
                    const paidOrders = collabOrders.filter(
                      (o) => o.status === "PAGO",
                    );
                    const grossRevenue = paidOrders.reduce(
                      (sum, o) => sum + (o.total || 0),
                      0,
                    );
                    const calculatedCommission =
                      grossRevenue * (collab.commission_rate / 100);
                    const totalPaid = collab.total_paid_commission || 0;
                    const pendingCommission = calculatedCommission - totalPaid;

                    // -- Lógica de Desempenho --
                    let totalEstimatedMins = 0;
                    let totalActualMins = 0;
                    collabOrders.forEach((o) => {
                      if (!o.completed_at) return;

                      const parseJson = (val: any) =>
                        typeof val === "string" ? JSON.parse(val) : val || {};
                      const timestamps = parseJson(o.stage_timestamps);

                      const startString =
                        timestamps.EM_ANDAMENTO ||
                        timestamps.LAVANDO ||
                        o.created_at;
                      if (!startString) return;

                      const created = new Date(startString).getTime();
                      const completed = new Date(o.completed_at).getTime();
                      let actualMins = (completed - created) / 60000;

                      if (actualMins <= 0) return;
                      actualMins = Math.max(1, actualMins);

                      const svcs = (o.services as any[]) || [];
                      const estMins = svcs.reduce(
                        (acc, s) => acc + (Number(s.time_minutes) || 0),
                        0,
                      );

                      if (estMins > 0) {
                        totalEstimatedMins += estMins;
                        totalActualMins += actualMins;
                      }
                    });

                    let efficiencyScore = 0;
                    if (totalActualMins > 0) {
                      efficiencyScore =
                        (totalEstimatedMins / totalActualMins) * 100;
                      if (efficiencyScore > 500) efficiencyScore = 500;
                    }

                    const isGestor = true;

                    return (
                      <div className="mt-4 pt-4 border-t border-slate-800">
                        {isGestor && efficiencyScore > 0 && (
                          <div className="mb-5 bg-slate-950/40 p-3 rounded-lg border border-slate-800/50">
                            <div className="flex justify-between text-xs mb-1.5">
                              <span className="text-slate-400 font-medium">
                                Eficiência (Tempo Estimado x Real)
                              </span>
                              <span
                                className={`font-bold ${
                                  efficiencyScore < 80
                                    ? "text-red-400"
                                    : efficiencyScore >= 500
                                      ? "text-purple-400"
                                      : efficiencyScore > 130
                                        ? "text-purple-400"
                                        : "text-emerald-400"
                                }`}
                              >
                                {efficiencyScore >= 500
                                  ? "+500%"
                                  : `${efficiencyScore.toFixed(0)}%`}
                              </span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-900 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  efficiencyScore < 80
                                    ? "bg-red-500"
                                    : efficiencyScore > 130
                                      ? "bg-purple-500"
                                      : "bg-emerald-500"
                                }`}
                                style={{
                                  width: `${Math.min(100, efficiencyScore)}%`,
                                }}
                              />
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2 leading-tight">
                              {efficiencyScore < 80
                                ? "⚠️ Atrasando o fluxo (mais lento que a estimativa do serviço)."
                                : efficiencyScore > 130
                                  ? "🚀 Muito rápido (verifique a qualidade ou se o tempo estimado está alto)."
                                  : "✅ Dentro do esperado. Operação saudável."}
                            </p>
                          </div>
                        )}

                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">
                              Comissão Acumulada
                            </span>
                            <span className="font-semibold text-slate-200">
                              R$ {calculatedCommission.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">
                              Total Já Quitado
                            </span>
                            <span className="font-semibold text-slate-200">
                              R$ {totalPaid.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm pt-2 border-t border-slate-800 border-dashed">
                            <span className="font-bold text-slate-300">
                              Saldo Pendente
                            </span>
                            <span
                              className={`font-bold ${
                                pendingCommission > 0
                                  ? "text-amber-400"
                                  : "text-emerald-400"
                              }`}
                            >
                              R$ {pendingCommission.toFixed(2)}
                            </span>
                          </div>
                        </div>

                        <Button
                          disabled={pendingCommission <= 0 || !isAtivo}
                          onClick={() => {
                            setPayCollabId(collab.id);
                            setPayAmount(Number(pendingCommission.toFixed(2)));
                          }}
                          className={`w-full font-bold gap-2 ${
                            pendingCommission > 0
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : "bg-slate-800 text-slate-500"
                          }`}
                        >
                          <DollarSign size={16} />
                          Pagar Comissão
                        </Button>
                      </div>
                    );
                  })()}
                </>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
      {collaborators.length === 0 && (
        <div className="col-span-full bg-slate-900 border border-slate-800 border-dashed rounded-xl p-12 text-center">
          <Users className="mx-auto text-slate-600 mb-3" size={48} />
          <h3 className="text-xl font-bold text-slate-300">
            Nenhum funcionário cadastrado
          </h3>
          <p className="text-slate-500 mt-1">
            Clique no botão acima para adicionar sua equipe e começar a gerar
            comissões.
          </p>
        </div>
      )}
    </div>
  );
}
